/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 모집일정을 슬라이드로 보여주는 컴포넌트
 * 버전 : 1.0
 * 파일명 : ScheduleBoard.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * 수정 : 2025-07-02, 강동형 : 저작권 추가, 비어있을때 이미지 추가
 * ================================================================
 * 수정 : 2025-07-03, 강동형 : 10개까지만 반환하도록 변경
 *================================================================*/

import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import '../../assets/styles/ScheduleBoard.css'; // CSS 파일을 import
import { Link, useNavigate } from 'react-router-dom';
import { ArmyRecruitDataType, LoadArmyRecruit } from '../recruitment/LoadArmyRecruit';
import { useRecruitStore } from 'src/stores/RecruitStore';
import { CalendarEvent } from '../adminRecruit/AdRecruitCalendar';
import { Icon_EmptyCalendar } from '../common/CommonIcons';

const ScheduleContainer = styled.div`
  position: relative;
  margin: 0 auto;
  background: #E9E9E9;
`;

const ScheduleBox = styled.div`
  padding: 1rem;
  overflow: hidden;
  background-color: #508B9C;
  min-height: 207px;
`;

const ScheduleHeader = styled.div`
  font-weight: bold;
  font-size: large;
  color: white;
`;

const InnerScheduleBox = styled.div`
  display: flex;
  cursor: grab; /* 드래그 가능한 커서 표시 */
  transition: transform 0.2s ease; /* 부드러운 드래그 이동 */
  will-change: transform; /* 성능 최적화를 위한 설정 */
  margin-bottom: 0.5rem;
  overflow-x: auto; // ✅ 추가
  &.active {
    cursor: grabbing; /* 드래그 중인 커서 표시 */
  }
`;

const ScheduleItem = styled.div`
  box-sizing: border-box;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 5px;
  margin-right: 1rem;
  white-space: nowrap;
  min-width: 245px;
`;

const ScheduleText = styled.p`
  margin: 0.2rem;
  line-height: 1;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
`;

const SmallBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem; // 박스 사이의 간격을 설정
`;

const ScheduleKindBox = styled.div`
  // background-color: #F5F5F5; // 회색 배경
  background: #F4F4F4;
  // color: #1976FF; // 파란색 글씨
  color: #035324;
  display: inline-block;
  padding: 0.1rem 0;
  border-radius: 5px;
  font-size: 0.8rem; // 작은 글씨;
  min-width: 46px;
  text-align: center;
`;

const ScheduleDdayBox = styled.div`
  // background-color: #1976FF; // 파란색 배경
  background: rgba(5, 136, 87, 0.90);
  color: white; // 흰색 글씨
  display: inline-block;
  padding: 0.1rem 0.3rem;
  border-radius: 5px;
  font-size: 0.8rem; // 작은 글씨;
  min-width: 42px;
  text-align: center;
`;

const ScheduleDescription = styled.p`
  font-weight: 500;
  margin: 0.2rem 0;
  margin-bottom : 1rem;;

    /* 텍스트 넘침 처리 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScheduleDeadline = styled.p`
  font-size: 12px; // 작은 글씨
  font-weight: 500;
  margin: 0.5rem 0;;
  color: gray;
`;

const ScheduleHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  a {
    text-decoration: none; // 링크의 밑줄을 제거합니다.
    color: white; // 링크의 색상을 검정색으로 설정합니다.
    font-size: 14px; // 링크의 글꼴 크기를 설정합니다.
  }
  padding-bottom: 16px;
`

const EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  margin-top: 14px;
  width: 100%;
  height: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #444;
`
type schedulesEvent = {
  kind: string,
  dday: string,
  description: string,
  deadline: string,
  announce_id : number,
}
 
const ScheduleBoard = () => {
  const navigate = useNavigate()

  const innerScheduleBoxRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // ✅ 드래그 상태용 상태 추가
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchScrollLeft, setTouchScrollLeft] = useState(0);
  const [data,setData] = useState<ArmyRecruitDataType[]>([])
  const setField = useRecruitStore((state)=>state.setField)
  const today = new Date()
  today.setHours(0, 0, 0, 0);
  const convertToSchdule = (data:ArmyRecruitDataType[]): schedulesEvent[]=>{
    return data.map(item => {
      const endDate = new Date(item.application_edate); // 문자열을 Date로 변환
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // ms → 일수 변환

      if (diffDays < 0 || diffDays > 60) return null;

      return {
        kind: item.recruitment_type,
        dday: diffDays === 0 ? 'D-day' : `D-${Math.abs(diffDays)}`,
        description: item.title,
        deadline: `서류접수마감일 ${item.application_edate.replaceAll('-', '.')}`,
        announce_id : item.announce_id
      };
    })
    .filter((event): event is schedulesEvent => event !== null).slice(0, 10); // ✅ 최대 10개까지만 반환
  }

  useEffect(() => {
    const today = new Date();
    let nextMonth = today.getMonth() + 2; // getMonth()는 0부터 시작하므로 +1로 현재월, +2로 다음달
    let nextYear = today.getFullYear();

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }

    const fetchData = async () => {
      const response = await LoadArmyRecruit({year:nextYear,month:nextMonth, limit_cnt:10});      
      setData(response.data)
    }
    fetchData()
  }, []);
  const schedules = convertToSchdule(data)

  // ✅ 터치 시작
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    innerScheduleBoxRef.current?.classList.add('active');

    setTouchStartX(e.touches[0].pageX);
    setTouchScrollLeft(innerScheduleBoxRef.current?.scrollLeft ?? 0);
  };

  // ✅ 터치 이동
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX;
    const walk = (x - touchStartX) * 4; // 스크롤 속도
    if (innerScheduleBoxRef.current) {
      innerScheduleBoxRef.current.scrollLeft = touchScrollLeft - walk;
    }
  };

  // ✅ 터치 끝
  const handleTouchEnd = () => {
    setIsDragging(false);
    innerScheduleBoxRef.current?.classList.remove('active');
  };

  useEffect(() => {
    const innerBox = innerScheduleBoxRef.current;
    if (!innerBox) return;

    const cloneItems = () => {
      const items = Array.from(innerBox.children);
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        innerBox.appendChild(clone);
      });
    };

    cloneItems();
  }, []);

  const onClickToRecruit = (schedule_id:number)=>{
    const targetItem = data.find((item) => item.announce_id === schedule_id);

    if (!targetItem) return; // targetItem이 없으면 early return

    const convertToEvents = (data: ArmyRecruitDataType[]): CalendarEvent[] => {
      return data.map(item => {
        // 먼저 item.etc를 사용해 기존의 이벤트를 변환
        const events = item.schedules.map(schedule => {
          return {
            title: item.title,
            start: new Date(schedule.start_date),
            end: new Date(schedule.end_date),
            announce_id: item.announce_id,
            course_type: schedule.course_name === '입영일' || schedule.course_name === '임관일' || schedule.course_name === '접수일정' ? schedule.course_name : '추가과정',
            course_name: schedule.course_name,
            course_status:item.status,
            quilify : item.requirements,
            urlLink : item.source_url,
            sessionData: item.schedules,
            recruitment_type : item.recruitment_type
          };
        });
        return [...events];
      }).flat();
    };
    const convertedEvents = convertToEvents([targetItem])[0];

    setField('selected',convertedEvents)
    setField('isOpen',true)
    navigate('/recruitment')
  }

  return (
    <ScheduleContainer>
      <ScheduleBox>
        <ScheduleHeaderWrapper>
          <ScheduleHeader>육군 모집 일정</ScheduleHeader>
          <Link to="/recruitment">더보기</Link>
        </ScheduleHeaderWrapper>
        {schedules.length === 0 ? 
          <EmptyWrapper>
            <Icon_EmptyCalendar/>
            <span>최근 60일 이내 마감 예정인 모집공고가 없습니다.</span>
          </EmptyWrapper>
        :
          <InnerScheduleBox
            ref={innerScheduleBoxRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={(e) => { if (isDragging) e.preventDefault(); }} // 드래그 중 클릭 방지
          >
            {schedules.map((schedule, index) => (
              <ScheduleItem key={index} onClick={()=>onClickToRecruit(schedule.announce_id)}>
                <SmallBoxContainer>
                  <ScheduleKindBox>
                    <ScheduleText>{schedule.kind}</ScheduleText>
                  </ScheduleKindBox>
                  <ScheduleDdayBox>
                    <ScheduleText>{schedule.dday}</ScheduleText>
                  </ScheduleDdayBox>
                </SmallBoxContainer>
                <ScheduleDescription>{schedule.description}</ScheduleDescription>
                <ScheduleDeadline>{schedule.deadline}</ScheduleDeadline>
              </ScheduleItem>
            ))}
            {/* {schedules.map((schedule, index) => (
              <ScheduleItem key={`clone-${index}`}>
                <SmallBoxContainer>
                  <ScheduleKindBox>
                    <ScheduleText>{schedule.kind}</ScheduleText>
                  </ScheduleKindBox>
                  <ScheduleDdayBox>
                    <ScheduleText>{schedule.dday}</ScheduleText>
                  </ScheduleDdayBox>
                </SmallBoxContainer>
                <ScheduleDescription>{schedule.description}</ScheduleDescription>
                <ScheduleDeadline>{schedule.deadline}</ScheduleDeadline>
              </ScheduleItem>
            ))} */}
          </InnerScheduleBox>
        }
      </ScheduleBox>
    </ScheduleContainer>
  );
};

export default ScheduleBoard;